/**
 * What the orders screens read from hg-order.
 *
 * Every key starts with `ORDERS_KEY`, so an action that changes a visit
 * invalidates the lot in one call: the order, the lists it is in, the queue,
 * the counts on the tabs.
 *
 * The staff stream says when to read them again (`./live.ts`). The board and
 * an open order are read with `useLiveQuery`, which also asks by itself as a
 * backstop, and whenever the window gets the focus back.
 */
import { keepPreviousData, queryOptions } from "@tanstack/react-query";

import { orderApi, unwrap } from "@/shared/api/clients";
import { useLiveQuery } from "@/shared/live";

import type { Channel, OrderSort, OrderStatus, PaymentMethod, VisitStatus } from "./types";

export const ORDERS_KEY = ["order", "staff"] as const;

/** How long an answer is taken for current when a screen that reads it opens. */
const FRESH_MS = 7_500;

/** Rows of a board page. */
export const PER_PAGE = 25;

/** A tab of the board hg-order keeps: one kind of "now". */
export type ServerTab = "live" | "scheduled" | "finished_today" | "stuck";

/** The filters hg-order's list of orders has. There are no others. */
export interface OrdersFilter {
  tab?: ServerTab;
  status?: OrderStatus;
  visit_status?: VisitStatus;
  channel?: Channel;
  payment_method?: PaymentMethod;
  area_id?: string;
  /** Made at or after this moment, and before that one. */
  created_from?: string;
  created_to?: string;
  sort?: OrderSort;
  q?: string;
}

/**
 * A page of the list of orders. Asked for in the request's body
 * (`POST /v2/admin/orders/search`, the list of `GET /v2/admin/orders` by the
 * same names): what the list is searched for may be a name or the end of a
 * phone, and is then in no address and no log.
 */
export function ordersQuery(filter: OrdersFilter, page: number, perPage: number = PER_PAGE) {
  return queryOptions({
    queryKey: [...ORDERS_KEY, "orders", filter, page, perPage] as const,
    queryFn: ({ signal }) =>
      unwrap(
        orderApi.POST("/v2/admin/orders/search", {
          body: { ...filter, page, per_page: perPage },
          signal,
        }),
      ),
    staleTime: FRESH_MS,
    placeholderData: keepPreviousData,
  });
}

/**
 * The questions asked of clients after a specialist ended their course
 * early, the newest first, within the caller's areas: `finished: false` is
 * the signal, the courses a client says were not finished.
 */
export function courseChecksQuery(
  filter: { finished?: boolean },
  page: number,
  perPage: number = PER_PAGE,
) {
  return queryOptions({
    queryKey: [...ORDERS_KEY, "course-checks", filter, page, perPage] as const,
    queryFn: ({ signal }) =>
      unwrap(
        orderApi.GET("/v2/admin/course-checks", {
          params: { query: { ...filter, page, per_page: perPage } },
          signal,
        }),
      ),
    staleTime: FRESH_MS,
    placeholderData: keepPreviousData,
  });
}

/** How many rows of each kind the board's "needs attention" reads at once. */
export const ATTENTION_ROWS = 100;

/**
 * The orders with a visit somebody has that does not move, the longest in
 * its status first: half of what needs a person. hg-order tells them by its
 * thresholds (`stuckSettingsQuery`), as they are when asked.
 */
export const stuckOrdersQuery = (areaId?: string) =>
  ordersQuery(
    { tab: "stuck", sort: "status_since", ...(areaId === undefined ? {} : { area_id: areaId }) },
    1,
    ATTENTION_ROWS,
  );

/**
 * After how long a visit somebody has counts as stuck: the norm a stuck visit
 * is shown against. Administration sets the same numbers: one copy for both.
 */
export { stuckSettingsQuery } from "@/shared/reference/stuck-visits";

/**
 * How an order's visits were looked for: every search, and everybody each
 * offered the visit to, with what came of it.
 */
export function dispatchQuery(orderId: string) {
  return queryOptions({
    queryKey: [...orderKey(orderId), "dispatch"] as const,
    queryFn: async ({ signal }) => {
      const { data } = await unwrap(
        orderApi.GET("/v2/admin/orders/{order_id}/dispatch", {
          params: { path: { order_id: orderId } },
          signal,
        }),
      );
      return data;
    },
    staleTime: FRESH_MS,
  });
}

/** What a client's orders add up to, within the caller's areas: how many, and how they stand. */
export function clientSummaryQuery(userId: string) {
  return queryOptions({
    queryKey: [...ORDERS_KEY, "client-summary", userId] as const,
    queryFn: async ({ signal }) => {
      const { data } = await unwrap(
        orderApi.GET("/v2/admin/clients/{user_id}/summary", {
          params: { path: { user_id: userId } },
          signal,
        }),
      );
      return data;
    },
    staleTime: 60_000,
  });
}

/**
 * A visit's road: the address, its moments, and the points the specialist's
 * phone took on the way and at it. hg-order writes every read of it to its
 * log, so it is asked only when somebody asks to see it.
 */
export function visitRoadQuery(visitId: string) {
  return queryOptions({
    queryKey: [...ORDERS_KEY, "road", visitId] as const,
    queryFn: async ({ signal }) => {
      const { data } = await unwrap(
        orderApi.GET("/v2/admin/visits/{visit_id}/track", {
          params: { path: { visit_id: visitId } },
          signal,
        }),
      );
      return data;
    },
    staleTime: 60_000,
  });
}

/**
 * The queue of visits the search could not place, the longest wait first:
 * the other half of what needs a person. hg-order serves it whole, a page at
 * a time; it takes no filter.
 */
export function openEscalationsQuery(page = 1, perPage: number = PER_PAGE) {
  return queryOptions({
    queryKey: [...ORDERS_KEY, "escalations", "open", page, perPage] as const,
    queryFn: ({ signal }) =>
      unwrap(
        orderApi.GET("/v2/admin/escalations", {
          params: { query: { state: "open", page, per_page: perPage } },
          signal,
        }),
      ),
    staleTime: FRESH_MS,
    placeholderData: keepPreviousData,
  });
}

/**
 * How many orders each tab of the board holds, and how many visits wait in
 * the queue: one request for all of them, within the caller's areas or one
 * of them. The sidebar's badge reads the same answer.
 */
export function boardCountsQuery(areaId?: string) {
  return queryOptions({
    queryKey: [...ORDERS_KEY, "counts", areaId ?? null] as const,
    queryFn: async ({ signal }) => {
      const { data } = await unwrap(
        orderApi.GET("/v2/admin/orders/counts", {
          params: { query: areaId === undefined ? {} : { area_id: areaId } },
          signal,
        }),
      );
      return data;
    },
    staleTime: FRESH_MS,
    placeholderData: keepPreviousData,
  });
}

export function orderKey(orderId: string) {
  return [...ORDERS_KEY, "order", orderId] as const;
}

export function orderQuery(orderId: string) {
  return queryOptions({
    queryKey: orderKey(orderId),
    queryFn: async ({ signal }) => {
      const { data } = await unwrap(
        orderApi.GET("/v2/admin/orders/{order_id}", {
          params: { path: { order_id: orderId } },
          signal,
        }),
      );
      return data;
    },
    staleTime: FRESH_MS,
  });
}

/** Who could be sent to a visit, the best fits first; `q` looks for one by name or phone. */
export function candidatesQuery(visitId: string, q: string) {
  return queryOptions({
    queryKey: [...ORDERS_KEY, "candidates", visitId, q] as const,
    queryFn: async ({ signal }) => {
      const { data } = await unwrap(
        orderApi.GET("/v2/admin/visits/{visit_id}/candidates", {
          params: { path: { visit_id: visitId }, query: q === "" ? {} : { q } },
          signal,
        }),
      );
      return data;
    },
    staleTime: FRESH_MS,
    placeholderData: keepPreviousData,
  });
}

/** The reasons a visit is cancelled for, every list: the dialog picks staff's. */
export const cancelReasonsQuery = queryOptions({
  queryKey: [...ORDERS_KEY, "cancel-reasons"] as const,
  queryFn: async ({ signal }) => {
    const { data } = await unwrap(orderApi.GET("/v2/admin/cancel-reasons", { signal }));
    return data;
  },
  staleTime: 10 * 60_000,
});

/**
 * How many orders need a person now: the visits the search could not place,
 * and the ones somebody has that do not move. The rail's number. Undefined
 * until known.
 */
export function useAttentionCount(): number | undefined {
  return useLiveQuery({
    ...boardCountsQuery(),
    select: (counts) => counts.needs_help + counts.stuck,
  }).data;
}

/**
 * What one thing typed finds among orders, specialists and clients, a few of
 * each: the top bar's search. Asked in the request's body, so that a name or
 * a phone is in no address and no log.
 */
export function searchQuery(q: string) {
  return queryOptions({
    queryKey: [...ORDERS_KEY, "search", q] as const,
    queryFn: async ({ signal }) => {
      const { data } = await unwrap(
        orderApi.POST("/v2/admin/search", { body: { q, limit: SEARCH_LIMIT }, signal }),
      );
      return data;
    },
    staleTime: FRESH_MS,
    // What was typed is not kept longer than the list that shows it.
    gcTime: 0,
  });
}

/** How many hits of each kind the search box shows. */
const SEARCH_LIMIT = 5;

/**
 * The id of the order with this number, or null. `q` also matches the end of
 * a client's phone, so only a row whose own number is the one asked for
 * counts.
 */
export async function findOrderByNumber(number: string): Promise<string | null> {
  const { data } = await unwrap(
    orderApi.GET("/v2/admin/orders", { params: { query: { q: number, page: 1, per_page: 10 } } }),
  );
  return data.find((row) => row.display_id === number)?.id ?? null;
}
